import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import {
  customElement,
  property,
  queryAssignedElements,
} from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import componentStyles from './se-career-card.css.lit';
import { classMap } from 'lit/directives/class-map.js';
import { MobileController } from '../../controllers/mobile-controller';
import '../se-heading/se-heading';
import { AllowedHeadingLevels } from '../se-heading/config';

/**
 * The SeCareerCard component
 * @element se-career-card
 */
@customElement('se-career-card')
export class SeCareerCard extends OutlineElement {
  private mobileController = new MobileController(this);

  static styles: CSSResultGroup = [componentStyles];

  @property({ type: String })
  department: string;

  @property({ type: String, attribute: 'office-location'})
  officeLocation: string; 

  @property({ type: String, attribute: 'card-title' })
  cardTitle: string;

  @property({ type: String })
  date: string;

  @property({ type: String })
  url: string;

  @property({ type: String, attribute: 'heading-level' })
  headingLevel: AllowedHeadingLevels = 'h3';

  @property({ type: Boolean })
  hasImage: boolean;

  @queryAssignedElements({ slot: 'image' })
  imageSlot: Array<HTMLElement>;

  // provides unique id number to pass to elements.
  seed: string = Math.floor(Math.random() * 10000).toString();

  render(): TemplateResult {
    return html`
      <article
        class="${classMap({'career-card': true})}"
        @click="${this.clickHandler}"
        aria-labelledby="link-${this.seed}"
      >
        <div class="career-card--content">
          <div 
            class="career-card--info ${this.hasImage
              ? 'has-image'
              : 'no-image'}"
          >
            ${this.tabTemplate()}  

            <a
              class="career-card--link"
              id="link-${this.seed}"
              href="${this.url}"
            >
              ${this.headingTemplate()}
            </a>

            ${this.date 
              ? html`<span class="career-card--date">Posted: ${this.date}</span>`
              : null
            }

            ${this.department 
              ? html`<span class="career-card--department" id="department-${this.seed}">
              Department: ${this.department}
              </span>`
              : null
            }

            ${this.officeLocation
              ? html `<!-- Updated property name -->
              <span class="career-card--location" id="location-${this.seed}">
              ${this.officeLocation.toUpperCase()}
              </span>`
              : null
            }

          </div>
          <div class="career-card--image" ?hidden="${!this.hasImage}">
            <slot @slotChange="${this.requestUpdate}" name="image"></slot>
          </div>
        </div>
      </article>
    `;
  }

  headingTemplate() {
    const isMobile = this.mobileController.isMobile;
    return html` <se-heading
          level="${ifDefined(this.headingLevel)}"
          level-size="${isMobile ? 'base' : 'lg'}"
          class="career-card--title"
          >${this.cardTitle}</se-heading
        >`;
  }

  tabTemplate() {
    return html`<span 
      class="career-card--type" 
      id="type-${this.seed}"
      >POSITION</span
    >`;
  }

  clickHandler() {
    (
      this.shadowRoot?.querySelector(`#link-${this.seed}`) as HTMLAnchorElement
    ).click();
  }

  firstUpdated() {
    this.hasImage = !!this.imageSlot.length;
  }
}
